<script setup lang="ts">
import {
  ArrowLeftCircleIcon,
  DocumentTextIcon,
  ClockIcon,
  UserIcon,
  CalendarIcon,
} from "@heroicons/vue/24/outline";

import { useTestimony } from '@/composables/useTestimony';

const route = useRoute();
const router = useRouter();
const { tourSlug } = route.params;

// Initialize tour with null
const tour = await useTour(tourSlug.toString());

const getCurrrentTourPrice = computed(() => {
  if (!tour.value || !tour.value.prices || tour.value.prices.length === 0) {
    return 0;
  }

  // Get the last element in the prices array
  const lastPrice = tour.value.prices[tour.value.prices.length - 1];

  // @ts-ignore: FIXME: resolve TS: Object is possibly 'undefined'.
  return lastPrice?.price ?? 0;
});


// Swiper
import { register } from "swiper/element/bundle";
import IconCar from "~/assets/icons/iconCar.vue";

register();

// back navigation
const handleNavigation = () => {
  if (window.history.length > 1) {
    // User has history
    router.go(-1);
  } else {
    // User does not have history go to home page
    router.push("/");
  }
};

// booking options
const cartStore = useCartStore();

const optionsPrices = tour.value?.prices;
const optionsPricesWithCount =
  optionsPrices?.map((item) => ({
    ...(typeof item === "object" ? item : {}),
    count: 0,
  })) || [];

const isBookingOptionsOpen = ref(false);

const handleBookingOptions = () => {
  const { id, title } = tour.value;

  cartStore.addProduct({
    product_id: id,
    title,
    // @ts-ignore: Ignore TypeScript type checking for this line todo
    participants: optionsPricesWithCount,
    participant_details: {
      hotel_address: "",
    },
    itinerary: Array.isArray(tour.value?.itinerary) ? tour.value.itinerary : [],
  });

  isBookingOptionsOpen.value = !isBookingOptionsOpen.value;
};

// wishlist
const wishlistStore = useWishlistStore();
const { toggleWishlist } = wishlistStore;
const wishlist = wishlistStore.wishlist;

const isWishlisted = computed(() => {
  // check the slug is in Wishlist array state
  const slug = tourSlug.toString();
  // find in array
  return wishlist.includes(slug);
});

// testimony rate
const { averageRating, fetchTestimony } = useTestimony();
onMounted(() => {
  fetchTestimony(tour.value.testimonies);
})
</script>

<template>
  <div
    class="absolute top-0 z-30 h-24 w-full bg-gradient-to-b-black px-4 pt-2.5"
  >
    <div class="flex justify-between">
      <button
        @click="handleNavigation"
        class="flex items-center space-x-2 text-white"
      >
        <ArrowLeftCircleIcon class="w-6 h-6 heroicon-sw-1" />
        <span>Back</span>
      </button>
      <WishlistButton
        :model-value="isWishlisted"
        @update:model-value="toggleWishlist(tourSlug.toString())"
      />
    </div>
  </div>

  <swiper-container
    :pagination="true"
    :autoplay="{
      delay: 8000,
      disableOnInteraction: false,
    }"
    :loop="true"
  >
    <swiper-slide v-for="photo in tour?.photos" :key="photo.id">
      <img :src="photo.path" alt="photo" class="object-cover" />
    </swiper-slide>
  </swiper-container>

  <div class="mt-4 px-[18px] pb-10 flex flex-col justify-center items-center">
    <div class="flex flex-col w-full gap-2 md:w-10/12">
      <h1 class="text-lg font-semibold leading-6 text-gray-800">
        {{ tour?.title }}
      </h1>

      <div class="flex items-center gap-1 text-gray-500">
        <svg
          width="10"
          height="14"
          viewBox="0 0 10 14"
          fill="none"
          class="fill-gray-500"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M5.33329 13.6666C7.24395 13.6666 9.99996 8.24397 9.99996 4.99998C9.99996 2.42265 7.91062 0.333313 5.33329 0.333313C2.75596 0.333313 0.666626 2.42265 0.666626 4.99998C0.666626 8.24397 3.42263 13.6666 5.33329 13.6666ZM5.33329 3.33335C6.25377 3.33335 6.99996 4.07955 6.99996 5.00002C6.99996 5.9205 6.25377 6.66669 5.33329 6.66669C4.41282 6.66669 3.66663 5.9205 3.66663 5.00002C3.66663 4.07955 4.41282 3.33335 5.33329 3.33335Z"
          />
        </svg>
        <span>{{ tour?.destination.title }}</span>
        <span class="mx-1 text-xs"> • </span>
        <span>{{ useHour(Number(tour?.duration)) }} </span>
        <span class="mx-1 text-xs"> • </span>
        <span> {{ tour?.activity_type[0].title }} Activity </span>
      </div>
      <div class="flex items-center gap-1">
        <img v-for="i in 5" :key="i" :src="i <= averageRating ? '/icons/star.svg' : '/images/star_outline.png'"
              alt="star" width="15" />
        <!-- <span class="ml-2 text-sm">5 (12)</span> -->
      </div>
      <div>
        <span class="mr-0.5 font-semibold md:font-medium"
          >From US$ {{ getCurrrentTourPrice }}</span
        >
        <sup class="text-gray-500">per person</sup>
      </div>
    </div>

    <div class="w-full md:w-10/12">
      <!-- Short Description -->
      <div class="my-4" v-html="tour?.short_description"></div>
      <AccordionGroupTitle title="About this activity" />
      <div class="mt-4">
        <div class="relative mb-4 pl-9">
          <dt class="font-semibold text-gray-800">
            <component
              :is="CalendarIcon"
              class="absolute w-5 h-5 left-1 top-1 text-primary"
              aria-hidden="true"
            />
            Free cancelation
          </dt>
          <dd class="text-gray-600">Cancel anytime as you wish</dd>
        </div>

        <div class="relative mb-4 pl-9">
          <dt class="font-semibold text-gray-00">
            <component
              :is="DocumentTextIcon"
              class="absolute w-5 h-5 left-1 top-1 text-primary"
              aria-hidden="true"
            />
            Reserve now and pay as you go
          </dt>
          <dd class="text-gray-600">
            Keep your travel plan flexible -- book your spot and pay as you go.
          </dd>
        </div>

        <div class="relative mb-4 pl-9">
          <dt class="font-semibold text-gray-800">
            <component
              :is="ClockIcon"
              class="absolute w-5 h-5 left-1 top-1 text-primary"
              aria-hidden="true"
            />
            Duration {{ useHour(Number(tour.duration)) }}
          </dt>
          <dd class="text-gray-600">
            Check itinerary to see the actual time and duration.
          </dd>
        </div>

        <div class="relative mb-4 pl-9">
          <dt class="font-semibold text-gray-800">
            <component
              :is="UserIcon"
              class="absolute w-5 h-5 left-1 top-1 text-primary"
              aria-hidden="true"
            />
            Instructor
          </dt>
          <dd class="text-gray-600">English, Indonesian</dd>
        </div>

        <div class="relative mb-4 pl-9">
          <dt class="font-semibold text-gray-800">
            <component
              :is="IconCar"
              class="absolute w-5 h-5 left-1 top-1 text-primary"
              aria-hidden="true"
            />
            Pickup optional
          </dt>
          <dd class="">
            Please wait in the hotel lobby 10 minutes before your scheduled
            pickup time.
          </dd>
        </div>
      </div>
      <AccordionGroupTitle title="Details & Highlights" />
      <Accordion title="Highlights" :content="tour?.highlight" />
      <Accordion title="Full description" :content="tour?.description" />
      <!-- <Accordion title="Itinerary" :content="tour?.itinerary" /> -->
      <Accordion title="What's Included" :content="tour?.whats_included" />
      <Accordion title="What's Excluded" :content="tour?.whats_excluded" />
      <Accordion
        title="Pick-up / Meetup Information"
        :content="tour?.pickup_info"
      />
      <Accordion title="Term & Conditions" :content="tour?.term_condition" />
      <AccordionGroupTitle title="Prepare for the activity" />
      <Accordion title="What to bring" :content="tour?.before_book" />
      <AccordionGroupTitle title="Customer reviews" />
      <Testimony :tour="tour"/>
    </div>
  </div>
  <div
    class="sticky bottom-0 flex items-center w-full px-4 py-3 space-x-6 bg-gray-800"
  >
    <div class="w-full text-white">
      <span class="font-light">From </span><br />
      <span class="text-base font-semibold"
        >US$ {{ getCurrrentTourPrice }}</span
      >
      {{}}
      <span class="text-sm font-extralight">per person</span>
    </div>
    <button
      @click="handleBookingOptions"
      class="w-full py-2 font-semibold text-white rounded-lg bg-secondary"
    >
      Add To Cart
    </button>
  </div>

  <BookingOptions
    :open="isBookingOptionsOpen"
    @close="isBookingOptionsOpen = false"
    :tour="tour"
  />
</template>

<style scoped>
swiper-container::part(bullet) {
  background-color: white;
}

swiper-container::part(bullet-active) {
  background-color: white;
}
</style>
